/**
 * @module features/users/pages/UsersPage
 * @description Master User Administration Page fulfilling the 4-state contract:
 * loading skeleton, error with retry, empty state, and rich multi-device data view.
 * Connects ViewUserModal, EditUserModal, and CreateUserDrawer.
 */

import * as React from "react";
import { UserPlus, RefreshCw, AlertCircle, Users } from "lucide-react";
import { PageHeader } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { UserStatsCards } from "../components/UserStatsCards";
import { UserFilters } from "../components/UserFilters";
import { UserTable } from "../components/UserTable";
import { CreateUserDrawer } from "../components/CreateUserDrawer";
import { EditUserModal } from "../components/EditUserModal";
import { ViewUserModal } from "../components/ViewUserModal";
import { AdminDisableTotpModal } from "../components/AdminDisableTotpModal";
import { useUsers, useUserStats } from "../api/users-api";
import { UserDto } from "@/types/auth";

export function UsersPage() {
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(10);
  const [role, setRole] = React.useState("all");
  const [search, setSearch] = React.useState("");

  // Modals state
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<UserDto | null>(null);
  const [viewingUser, setViewingUser] = React.useState<UserDto | null>(null);
  const [disableTotpUser, setDisableTotpUser] = React.useState<UserDto | null>(null);

  const {
    data: usersData,
    isLoading: isUsersLoading,
    isError: isUsersError,
    error: usersError,
    refetch: refetchUsers,
    isFetching: isUsersFetching,
  } = useUsers({
    page,
    page_size: pageSize,
    role,
    search,
  });

  const {
    data: statsData,
    isLoading: isStatsLoading,
    refetch: refetchStats,
  } = useUserStats();

  const handleRoleChange = React.useCallback((newRole: string) => {
    setRole(newRole);
    setPage(1);
  }, []);

  const handleSearchChange = React.useCallback((newSearch: string) => {
    setSearch(newSearch);
    setPage(1);
  }, []);

  const handleRefreshAll = React.useCallback(() => {
    refetchUsers();
    refetchStats();
  }, [refetchUsers, refetchStats]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <PageHeader
        title="User Management"
        description="Manage super administrators, clients, 2FA credentials, and access credentials."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshAll}
              disabled={isUsersFetching}
              className="h-9 text-xs"
              title="Refresh users and platform statistics"
            >
              <RefreshCw
                className={`mr-1.5 h-3.5 w-3.5 text-muted-foreground ${
                  isUsersFetching ? "animate-spin" : ""
                }`}
              />
              <span>Refresh</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setIsCreateOpen(true)}
              className="h-9 text-xs shadow-sm bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
            >
              <UserPlus className="mr-1.5 h-4 w-4" />
              <span>Create User</span>
            </Button>
          </div>
        }
      />

      {/* KPI Metrics Summary (Compact with vibrant colors) */}
      <UserStatsCards stats={statsData} isLoading={isStatsLoading} />

      {/* Search & Filter Toolbar */}
      <UserFilters
        search={search}
        role={role}
        onSearchChange={handleSearchChange}
        onRoleChange={handleRoleChange}
      />

      {/* ------------------------------------------------------------- */}
      {/* 4-State Contract Rendering */}
      {/* ------------------------------------------------------------- */}
      {isUsersLoading ? (
        // State 1: 60fps Geometry-Matched Skeleton Loader
        <div className="rounded-xl border border-border bg-card overflow-hidden shadow-xs">
          <div className="p-4 border-b border-border/80 flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-8 w-24 rounded-lg" />
          </div>
          <div className="divide-y divide-border/60">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                </div>
                <Skeleton className="h-6 w-20 rounded-full hidden sm:block" />
                <Skeleton className="h-4 w-24 hidden md:block" />
                <Skeleton className="h-4 w-16 hidden lg:block" />
                <div className="flex gap-2">
                  <Skeleton className="h-8 w-8 rounded-md" />
                  <Skeleton className="h-8 w-8 rounded-md" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : isUsersError ? (
        // State 2: Error
        <div className="rounded-xl border border-danger/20 bg-danger-soft p-6 sm:p-8 text-center text-danger">
          <AlertCircle className="mx-auto h-8 w-8" />
          <h2 className="mt-3 font-semibold text-base text-danger">
            Failed to load users
          </h2>
          <p className="mt-1 text-xs text-danger/80 max-w-md mx-auto">
            {(usersError as any)?.errorDetails ||
              (usersError as any)?.message ||
              "An unexpected error occurred while communicating with the server."}
          </p>
          {(usersError as any)?.requestId && (
            <p className="mt-2 text-[11px] font-mono text-muted-foreground">
              Request ID: {(usersError as any)?.requestId}
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchUsers()}
            className="mt-4 bg-card text-foreground"
          >
            Retry Request
          </Button>
        </div>
      ) : usersData?.items.length === 0 && !search && role === "all" ? (
        // State 3: Empty
        <div className="rounded-xl border border-dashed border-border bg-card/50 p-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <Users className="h-6 w-6" />
          </div>
          <h2 className="mt-4 font-semibold text-base text-foreground">
            No users registered
          </h2>
          <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
            Get started by adding super administrators or client accounts to the
            platform.
          </p>
          <Button
            size="sm"
            onClick={() => setIsCreateOpen(true)}
            className="mt-4 text-xs"
          >
            <UserPlus className="mr-1.5 h-4 w-4" />
            Create First User
          </Button>
        </div>
      ) : (
        // State 4: Rich Multi-Device Data Grid
        <UserTable
          users={usersData?.items || []}
          pagination={usersData?.pagination}
          onPageChange={(newPage) => setPage(newPage)}
          onEditUser={(user) => setEditingUser(user)}
          onViewUser={(user) => setViewingUser(user)}
          onDisableTotp={(user) => setDisableTotpUser(user)}
        />
      )}

      {/* 1. Create User Drawer */}
      <CreateUserDrawer
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      {/* 2. Edit User Modal */}
      <EditUserModal
        user={editingUser}
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        onDisableTotp={(user) => setDisableTotpUser(user)}
      />

      {/* 3. View User Details Modal */}
      <ViewUserModal
        user={viewingUser}
        open={Boolean(viewingUser)}
        onClose={() => setViewingUser(null)}
        onEdit={(user) => {
          setViewingUser(null);
          setEditingUser(user);
        }}
        onDisableTotp={(user) => setDisableTotpUser(user)}
      />

      {/* 4. Super Admin Disable 2FA Confirmation Modal */}
      <AdminDisableTotpModal
        user={disableTotpUser}
        open={Boolean(disableTotpUser)}
        onClose={() => setDisableTotpUser(null)}
        onSuccess={(updatedUser) => {
          if (viewingUser?.id === updatedUser.id) {
            setViewingUser(updatedUser);
          }
          if (editingUser?.id === updatedUser.id) {
            setEditingUser(updatedUser);
          }
        }}
      />
    </div>
  );
}
